import { useState } from 'react';
import { useIntl } from 'react-intl';

import type { Area, Country, Locale } from '@/types/regionTypes';

import { Flex } from '@/components/Flex';
import { Icon } from '@/components/Icon';
import { LegacyButton } from '@/components/LegacyButton';
import { Typography } from '@/components/Typography';
import type { CountryCode, LanguageCodeEnum } from '@/graphql/saleor/api';
import { useIsMdDown } from '@/hooks/useMedia';
import { getCountrySelectorList } from '@/lib/countries';
import { DEFAULT_LANGUAGE } from '@/lib/regions';
import {
  countrySelectorMessages,
  InternationalHeaderMobile,
} from '@/messages/countrySelector';

import * as S from './CountrySelector.styles';

export interface CountrySelectorProps {
  onClose: () => void;
  onContinue: (
    countrySlug: CountryCode,
    languageSlug: LanguageCodeEnum,
  ) => void;
  regionsList: Area[];
  userCountry: Country;
  userLanguage: Locale;
  userRegion: Area;
}

export const CountrySelector = ({
  onClose,
  onContinue,
  regionsList,
  userCountry,
  userLanguage,
  userRegion,
}: CountrySelectorProps) => {
  const { formatMessage } = useIntl();
  const isMobileView = useIsMdDown();
  const defaultLanguage = DEFAULT_LANGUAGE;
  const [country, setCountry] = useState<Country>(userCountry);
  const [language, setLanguage] = useState<Locale>(userLanguage);
  const [region, setRegion] = useState<Area>(userRegion);

  const onRegionChange = (region: Area) => {
    setRegion(region);
    if (region.countries != null && region.countries.length > 0) {
      onCountryChange(region.countries![0]);
    } else {
      setCountry(userCountry);
    }
  };

  const onCountryChange = (
    event: Country | (EventTarget & HTMLSelectElement),
  ) => {
    let country: Country;
    if ((event as EventTarget & HTMLSelectElement)?.options) {
      const selectedOption = (event as EventTarget & HTMLSelectElement)
        ?.options[(event as EventTarget & HTMLSelectElement).selectedIndex];

      // Retrieve the data-country attribute value
      const dataCountry = selectedOption.getAttribute('data-country');

      // Parse the JSON string to get the country object
      country = JSON.parse(dataCountry as string);
    } else {
      country = event as Country;
    }

    country && setCountry(country);
    if (country.locales?.length > 0) {
      setLanguage(country.locales[0]);
    }
  };

  const onChangeLanguage = (event: EventTarget & HTMLSelectElement) => {
    const selectedOption = event.options[event.selectedIndex];

    const dataLocale = selectedOption.getAttribute('data-locale');

    const locale = JSON.parse(dataLocale as string);

    locale && setLanguage(locale);
  };

  const onClickContinue = () => {
    //TODO save user's locale in localstorage
    onContinue(country.slug as CountryCode, language.slug as LanguageCodeEnum);
    onClose();
  };

  const regionTiles = (
    <S.TilesContainer>
      {regionsList.map((r, index) => (
        <S.Tile
          key={r.code}
          data-test-id={`CountrySelectorTilesContainer-${index}`}
          isMobile={isMobileView}
          isSelected={r.code === region.code}
          onClick={() => onRegionChange(r)}
        >
          <Typography>{r.name}</Typography>
        </S.Tile>
      ))}
    </S.TilesContainer>
  );

  const languageTiles = (
    <S.TilesContainer>
      {(country.locales ?? [defaultLanguage])?.map((lang, index) => (
        <S.Tile
          key={lang.slug}
          data-test-id={`CountrySelectorLanguageTilesContainer-${index}`}
          isMobile={isMobileView}
          isSelected={lang.slug === language?.slug}
          onClick={() => setLanguage(lang)}
        >
          <Typography>{lang.name}</Typography>
        </S.Tile>
      ))}
    </S.TilesContainer>
  );

  const countrySelect = (
    <S.SelectContainer flex={2}>
      <S.Select
        data-testid='CountrySelectorSelectElement'
        name='country'
        onChange={(opt) => {
          opt && onCountryChange(opt.target);
        }}
        value={country ? country.slug : undefined}
      >
        {(
          getCountrySelectorList(region.countries as Country[], country) ?? []
        ).map((cty, idx: number) => {
          if (!(cty as Country).slug) {
            return (
              <option key={'region-selector-group-' + idx} disabled value=''>
                ───────────────────
              </option>
            );
          }
          return (
            <option
              key={(cty as Country).slug}
              data-country={JSON.stringify(cty)}
              value={(cty as Country).slug}
            >
              {(cty as Country).name}
            </option>
          );
        })}
      </S.Select>
      <S.ArrowBox>
        <Icon name='chevronDown' size='l' variant='regular' />
      </S.ArrowBox>
    </S.SelectContainer>
  );

  const languageSelect = (
    <S.SelectContainer flex={1}>
      <S.Select
        key={language.slug}
        name='language'
        onChange={(opt) => {
          opt && onChangeLanguage(opt.target);
        }}
        value={language ? language.slug : undefined}
      >
        {(country.locales?.length > 0
          ? country.locales
          : [defaultLanguage]
        ).map((locale) => (
          <option
            key={locale.slug}
            data-locale={JSON.stringify(locale)}
            value={locale.slug}
          >
            {locale.name}
          </option>
        ))}
        <S.ArrowBox>
          <Icon name='chevronDown' size='l' variant='regular' />
        </S.ArrowBox>
      </S.Select>
      <S.ArrowBox>
        <Icon name='chevronDown' size='l' variant='regular' />
      </S.ArrowBox>
    </S.SelectContainer>
  );

  const continueButton = (
    <LegacyButton
      colorVariant='primary'
      data-test-id='CountrySelectorLegacyButton'
      isFluid
      label={formatMessage(countrySelectorMessages.internationalCta)}
      onClick={() => onClickContinue()}
      style={{ marginTop: 'auto' }}
      variant='filled'
    />
  );

  return isMobileView ? (
    <S.Wrapper>
      <S.MobileContentWrapper>
        <S.CloseIcon
          data-test-id='CountrySelectorCloseIconMobile'
          isMobile={isMobileView}
          name='close'
          onClick={onClose}
          size='l'
          variant='regular'
        />
        <Typography
          data-testid='CountrySelectorInternationalHeaderMobile-Paragraph'
          marginBottom='s025'
          scale='h4'
          style={{ fontWeight: 'bold' }}
          textAlign='center'
          textTransform='uppercase'
        >
          <InternationalHeaderMobile />
        </Typography>
        <div data-test-id='CountrySelectorRegionTilesMobile'>{regionTiles}</div>
        <Flex data-test-id='CountrySelectorSelectsMobile' gridGap='s050'>
          {countrySelect}
          {languageSelect}
        </Flex>
        <div data-test-id='CountrySelectorContinueButtonMobile'>
          {continueButton}
        </div>
      </S.MobileContentWrapper>
    </S.Wrapper>
  ) : (
    <S.Wrapper>
      <S.ContentWrapper
        style={{ display: 'flex', flex: '1', maxWidth: '750px' }}
      >
        <Flex style={{ flex: '1' }}>
          <S.CloseIcon
            data-test-id='CountrySelectorCloseButton'
            isMobile={isMobileView}
            name='close'
            onClick={onClose}
            size='l'
            variant='regular'
          />
          <Flex
            data-test-id='CountrySelectorColumn'
            flexDirection='column'
            gridGap='s100'
          >
            <Flex data-test-id='CountrySelectorLocationFlex'>
              <Icon
                data-test-id='CountrySelectorLocationIcon'
                name='location'
                size='l'
                variant='regular'
              />
              <Typography
                data-test-id='CountrySelectorLocationText'
                pl='s050'
                scale='h4'
                style={{ fontWeight: 'bold' }}
                textTransform='uppercase'
              >
                {formatMessage(countrySelectorMessages.internationalLocation)}
              </Typography>
            </Flex>
            <div data-test-id='CountrySelectorRegionTiles'>{regionTiles}</div>
            <div data-test-id='CountrySelectorCountrySelect'>
              {countrySelect}
            </div>
          </Flex>
        </Flex>
        <Flex flex={1}>
          <Flex
            data-test-id='CountrySelectorLanguageFlex'
            flexDirection='column'
            gridGap='s100'
            style={{ width: '100%' }}
          >
            <Flex data-test-id='CountrySelectorLanguageIconFlex'>
              <Icon
                data-test-id='CountrySelectorLanguageIcon'
                name='language'
                size='l'
                variant='regular'
              />
              <Typography
                data-test-id='CountrySelectorLanguageText'
                pl='s050'
                scale='h4'
                style={{ fontWeight: 'bold' }}
                textTransform='uppercase'
              >
                {formatMessage(countrySelectorMessages.internationalLanguage)}
              </Typography>
            </Flex>
            <div data-test-id='CountrySelectorLanguageTiles'>
              {languageTiles}
            </div>
            <div data-test-id='CountrySelectorContinueButton'>
              {continueButton}
            </div>
          </Flex>
        </Flex>
      </S.ContentWrapper>
    </S.Wrapper>
  );
};
